import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface TagResourceCounts {
  devices: number;
  profiles: number;
  applications: number;
  configurations: number;
  patchPolicies: number;
  users: number;
  total: number;
}

export const useTagResourceCounts = (tagId: string) => {
  const [counts, setCounts] = useState<TagResourceCounts>({
    devices: 0,
    profiles: 0,
    applications: 0,
    configurations: 0,
    patchPolicies: 0,
    users: 0,
    total: 0
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchResourceCounts = async () => {
    if (!tagId) return;
    
    try {
      setIsLoading(true);
      setError(null);

      // Fetch resource counts in parallel
      const [deviceCount, profileCount, applicationCount, configurationCount, patchPolicyCount, userCount] = await Promise.all([
        supabase
          .from('device_tags')
          .select('id', { count: 'exact' })
          .eq('tag_id', tagId),
        
        supabase
          .from('profile_tags')
          .select('id', { count: 'exact' })
          .eq('tag_id', tagId),
        
        supabase
          .from('application_tags')
          .select('id', { count: 'exact' })
          .eq('tag_id', tagId),
        
        supabase
          .from('configuration_tags')
          .select('id', { count: 'exact' })
          .eq('tag_id', tagId),
        
        supabase
          .from('patch_policy_tags')
          .select('id', { count: 'exact' })
          .eq('tag_id', tagId),
        
        supabase
          .from('device_user_tags')
          .select('id', { count: 'exact' })
          .eq('tag_id', tagId)
      ]);

      const devices = deviceCount.count || 0;
      const profiles = profileCount.count || 0;
      const applications = applicationCount.count || 0;
      const configurations = configurationCount.count || 0;
      const patchPolicies = patchPolicyCount.count || 0;
      const users = userCount.count || 0;
      const total = devices + profiles + applications + configurations + patchPolicies + users;

      setCounts({
        devices,
        profiles,
        applications,
        configurations,
        patchPolicies,
        users,
        total
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch resource counts');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchResourceCounts();
  }, [tagId]);

  return { counts, isLoading, error, refetch: fetchResourceCounts };
};